import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Icon, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useAsyncFn } from 'react-use';
import { toast } from 'react-toastify';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import { SendFileForm } from 'components/CommonForms';
import {
  downloadTrainingSessionFile,
  getTrainingSessionFilesList,
  openTrainingSessionFile,
} from 'http/trainingSessions';
import { updateTrainingPartFileVisibility, uploadTrainingPartFile } from 'http/trainingPartFiles';
import { actions as modalActions } from 'redux/modal';
import { resolveApiErrorMessage, transformTableData } from 'utils';
import getTrainingPartFilesTableConfig from 'pages/TrainingModules/Edit/helpers/getTrainingPartFilesTableConfig';

const ACCEPTED_FILE_FORMAT = '.pdf, .pptx, .doc, .docx, .xlsx, .ppt, .jpeg, .jpg, .png';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

const mapFileRows = items =>
  (items || []).map(item => ({
    ...item,
    visibility: {
      id: item.id,
      isVisibleForStudent: item.isVisibleForStudent,
      isVisibleForTrainer: item.isVisibleForTrainer,
    },
  }));

const SessionFilesPanel = ({
  trainingSessionId,
  trainingPartId,
  canManageVisibility = false,
  canAddFiles = true,
  isReadOnly = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const [files, setFiles] = useState([]);

  const showError = useCallback(
    message => {
      toast.error(
        resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      );
    },
    [t],
  );

  const [filesState, fetchFiles] = useAsyncFn(async () => {
    return getTrainingSessionFilesList(trainingSessionId, {
      page: 0,
      pageSize: 100,
    });
  }, [trainingSessionId]);

  const refreshFiles = useCallback(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (trainingSessionId) {
      fetchFiles();
    }
  }, [fetchFiles, trainingSessionId]);

  useEffect(() => {
    if (!filesState.value?.error && !filesState.value?.statusCode) {
      setFiles(mapFileRows(filesState.value?.items));
    }
  }, [filesState.value]);

  const [visibilityState, updateVisibility] = useAsyncFn(
    async (fileId, data) => {
      const result = await updateTrainingPartFileVisibility(trainingPartId, fileId, data);
      if (!result?.error && !result?.statusCode) {
        refreshFiles();
        return result;
      }
      showError(result?.message);
      return result;
    },
    [refreshFiles, showError, trainingPartId],
  );

  const handleToggleVisibility = useCallback(
    (fileId, data) => {
      if (isReadOnly || !canManageVisibility) {
        return;
      }
      updateVisibility(fileId, data);
    },
    [canManageVisibility, isReadOnly, updateVisibility],
  );

  const tableConfig = useMemo(
    () =>
      getTrainingPartFilesTableConfig({
        showVisibility: canManageVisibility,
        handleToggleVisibility,
        isReadOnly: isReadOnly || !canManageVisibility,
      }),
    [canManageVisibility, handleToggleVisibility, isReadOnly],
  );

  const [addFileState, addFile] = useAsyncFn(
    async formData => {
      const rawFile = formData?.file;
      const file = Array.isArray(rawFile) ? rawFile[0] : rawFile;
      const result = await uploadTrainingPartFile(trainingPartId, file);
      if (!result?.error && !(result?.statusCode && result.statusCode >= 400)) {
        dispatch(modalActions.closeModal());
        refreshFiles();
        toast.success(t('trainingSessions.documentUploadSuccess', { defaultValue: 'Document uploaded' }));
        return result;
      }
      return {
        error: true,
        message: result?.message || 'errors.serverError',
        statusCode: result?.statusCode,
      };
    },
    [dispatch, refreshFiles, t, trainingPartId],
  );

  const handleAddDocument = () => {
    if (isReadOnly || !canAddFiles) {
      return;
    }
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('courseDetails.addDocumentModalTitle'),
        },
        <SendFileForm
          onSubmitForm={addFile}
          rejectAction={() => {
            dispatch(modalActions.closeModal());
          }}
          submitting={addFileState.loading}
          fileFormat={ACCEPTED_FILE_FORMAT}
        />,
      ),
    );
  };

  const [actionState, runFileAction] = useAsyncFn(async ({ action, fileId, fileName }) => {
    try {
      if (action === 'view') {
        await openTrainingSessionFile(trainingSessionId, fileId, fileName);
      } else {
        await downloadTrainingSessionFile(trainingSessionId, fileId, fileName);
      }
      return { success: true };
    } catch (error) {
      showError(error?.message);
      return error;
    }
  });

  const tableActions = useMemo(
    () => [
      {
        type: 'icon',
        icon: 'visibility',
        title: t('trainingPartFiles.actionView'),
        onClick: event => {
          const fileId = Number(event.currentTarget.dataset.key);
          const file = files.find(item => item.id === fileId);
          if (file) {
            runFileAction({ action: 'view', fileId, fileName: file.name });
          }
        },
      },
      {
        type: 'icon',
        icon: 'get_app',
        title: t('trainingPartFiles.actionDownload'),
        onClick: event => {
          const fileId = Number(event.currentTarget.dataset.key);
          const file = files.find(item => item.id === fileId);
          if (file) {
            runFileAction({ action: 'download', fileId, fileName: file.name });
          }
        },
      },
    ],
    [files, runFileAction, t],
  );

  const [columns, rows] = transformTableData(tableConfig, files);
  const isLoading = filesState.loading || actionState.loading || visibilityState.loading || addFileState.loading;
  const showAddButton = canAddFiles && !isReadOnly;

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t('trainingSessions.tabs.files')}
        </Typography>
        {showAddButton ? (
          <Button
            color="primary"
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={handleAddDocument}
            disabled={addFileState.loading}
          >
            {t('courseDetails.actionAddDocument')}
          </Button>
        ) : null}
      </Box>
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={rows}
          totalCount={files.length}
          rowsPerPage={files.length || 10}
          actions={tableActions}
        />
      </ModalBusyContainer>
    </Box>
  );
};

SessionFilesPanel.propTypes = {
  trainingSessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  trainingPartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  canManageVisibility: PropTypes.bool,
  canAddFiles: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};

export default SessionFilesPanel;
